import { useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SHELL } from '@/constants/testIds';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! How can we help you today?' },
  ]);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    const userMsg = message.trim();
    setMessages((m) => [...m, { role: 'user', text: userMsg }]);
    setMessage('');
    setSending(true);
    try {
      const r = await api('/api/v1/support/chat', {
        method: 'POST',
        body: { message: userMsg },
      });
      if (r.status === 200) {
        setMessages((m) => [...m, { role: 'bot', text: r.data.reply || '—' }]);
      } else {
        const err = String(r.data?.error ?? 'Support unavailable');
        setMessages((m) => [...m, { role: 'bot', text: err }]);
        toast.error(err);
      }
    } catch {
      toast.error('Support unavailable');
      setMessages((m) => [...m, { role: 'bot', text: 'Support unavailable' }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-40" data-testid={SHELL.supportWidget}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 right-0 w-80 bg-card border rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <Icons.MessageCircle className="h-4 w-4" />
                <h3 className="font-semibold text-sm">Support</h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="hover:opacity-80">
                <Icons.X className="h-4 w-4" />
              </button>
            </div>
            <div className="h-64 overflow-y-auto p-3 space-y-2 bg-muted/20">
              {messages.map((m, idx) => (
                <div
                  key={idx}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                      m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card border'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-card border px-3 py-2 rounded-lg text-sm text-muted-foreground">
                    Typing...
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSend()}
                placeholder="Ask about your account..."
                className="flex-1 px-3 py-2 bg-background border rounded-md text-sm outline-none focus:ring-2 focus:ring-ring"
              />
              <Button size="sm" onClick={() => void handleSend()} disabled={sending || !message.trim()}>
                <Icons.Send className="h-3 w-3" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Button
        onClick={() => setOpen(!open)}
        className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl"
        size="icon"
      >
        {open ? <Icons.X className="h-5 w-5" /> : <Icons.MessageCircle className="h-5 w-5" />}
      </Button>
    </div>
  );
}
